/**
 * M10 P2a1 (hull tiers spec §3.1, §4.3, §6.1; plan 2026-10-10-m10-p2 §3.1): the schema of `hull-lines.json`,
 * the authoring source for strategic hulls, and of `hull-order.json`, the append-only lock on `hulls.json` row
 * order. Nothing reads either at runtime in P2a1; P2a2's build-time compile and P2b2's derived tables do.
 * M10 P4a1a adds the P4a switches and tables (licences, royalty, embargo, the outlaw rule, canBuild, capacity
 * units, the L flag weight, yard levels per band, `sale`), all off or inert until P4a7a/P4a7b.
 *
 * Imports only `types.ts` and zod, never `strategic/schemas.ts`, so `schemas.ts` may import `HULL_BAND_IDS`
 * from here without a module cycle. The band ids are defined here because the worker must not import the
 * main-thread `src/data/schemas.ts` (`SHIP_BAND_IDS`); a test checks the two lists match.
 */
import { z } from 'zod'
import hullLinesJson from '../data/strategic/hull-lines.json'
import hullOrderJson from '../data/strategic/hull-order.json'
import { FACTION_IDS, HULL_CLASS_NAMES, ROLE_IDS } from './types'

/** Size bands, keyed like `src/data/bands.json` (S, M, L, XL). */
export const HULL_BAND_IDS = ['S', 'M', 'L', 'XL'] as const
export type HullBand = (typeof HULL_BAND_IDS)[number]

/** The 12 hull roles of spec §3.1 (the Capital is an XL station template, P6). Append-only: P2b2 indexes them. */
export const HULL_ROLE_IDS = [
  'utility',
  'interceptor',
  'gunship',
  'bomber',
  'escort',
  'miner',
  'salvager',
  'courier',
  'hauler',
  'tug',
  'patrol',
  'survey',
] as const
export type HullRoleId = (typeof HULL_ROLE_IDS)[number]

export const MAKER_KINDS = ['manufacturer', 'rebuilder', 'operator'] as const
export type MakerKind = (typeof MAKER_KINDS)[number]

/** The strategic doctrine multipliers of spec §4.3 (absent = 1). */
export const DOCTRINE_STATS = ['cargo', 'strength', 'toughness', 'buildCost'] as const
export type DoctrineStat = (typeof DOCTRINE_STATS)[number]

const idString = z.string().regex(/^[a-z][a-z0-9_]*$/)
const lineId = z.string().regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/)
const bandSchema = z.enum(HULL_BAND_IDS)
const factionSchema = z.enum(FACTION_IDS)
const hullRoleSchema = z.enum(HULL_ROLE_IDS)
const fraction = z.number().gt(0).max(1)
const multiplier = z.number().positive()

const sizeSchema = z
  .strictObject({
    tollClass: z.enum(HULL_CLASS_NAMES),
    claimable: z.boolean(),
    /** Mk I, II and III shares of the hull rating, rising, Mk III = 1. */
    mkFractions: z.tuple([fraction, fraction, fraction]),
    /** The Mk a new hull of this band ships with; absent = the career start fit. */
    stockMk: z.int().min(1).max(3).optional(),
    crew: z.tuple([z.int().nonnegative(), z.int().nonnegative()]),
    /** M10 P4a1a (spec §4.9): the lowest yard level that builds this band (`canBuild`). */
    minYardLevel: z.int().min(1),
    /** M10 P4a1a (spec §4.9): this band's orders skip the aged-order hold. Read by P4a1c; all false until P4b5. */
    agedHoldExempt: z.boolean(),
  })
  .refine((s) => s.mkFractions[0] < s.mkFractions[1] && s.mkFractions[1] < s.mkFractions[2] && s.mkFractions[2] === 1, {
    message: 'mkFractions must rise to 1',
  })
  .refine((s) => s.crew[0] <= s.crew[1], { message: 'crew must be [min, max]' })

/**
 * M10 P2a2: a role's rating template at one band (spec §4.3, §6.1). The compile multiplies `cargo`, `strength`,
 * `toughness` and each `buildCost` ware by the maker's doctrine, then applies the line hull's `overrides`. Ware ids
 * and the row shape are checked against `strategic/schemas.ts hullSchema` on the compiled row (this module can't
 * import `schemas.ts`). Empty in `hull-lines.json` until P4b1.
 */
const templateStatFields = {
  cargo: z.number().positive(),
  strength: z.number().positive(),
  toughness: z.number().positive(),
  buildCost: z.record(idString, z.number().positive()),
  buildSeconds: z.number().positive(),
  wreckLayout: idString,
  patrolShipDef: idString.optional(),
  escort: z.boolean().optional(),
  crew: z.int().positive().optional(),
  standingReq: z.number().min(-100).max(100).optional(),
}
/** The stats a line hull needs from its cell (all or none; a cell without them carries class ranges only). */
export const TEMPLATE_REQUIRED_STATS = ['cargo', 'strength', 'toughness', 'buildCost', 'buildSeconds', 'wreckLayout'] as const

const range = (lo: z.ZodNumber) => z.tuple([lo, lo]).refine(([a, b]) => a <= b, { message: 'a range is [min, max]' })

/**
 * M10 P4b1a1 (review M2, the P2 hand-off): the class ranges of the rows a cell types, replacing `fleet.json classes`
 * for templated rows and variants (`data.ts` load rule). `hold` in t, `price` in credits, `crewRange` in heads
 * (`crew` is already the template's crew stat). A cell with ranges and no stats types only variants.
 */
const templateRangeFields = {
  hold: range(z.number().nonnegative()).optional(),
  price: range(z.number().positive()).optional(),
  crewRange: range(z.int().min(1)).optional(),
}

const hullTemplateSchema = z
  .strictObject({
    cargo: templateStatFields.cargo.optional(),
    strength: templateStatFields.strength.optional(),
    toughness: templateStatFields.toughness.optional(),
    buildCost: templateStatFields.buildCost.optional(),
    buildSeconds: templateStatFields.buildSeconds.optional(),
    wreckLayout: templateStatFields.wreckLayout.optional(),
    patrolShipDef: templateStatFields.patrolShipDef,
    escort: templateStatFields.escort,
    crew: templateStatFields.crew,
    standingReq: templateStatFields.standingReq,
    ...templateRangeFields,
  })
  .refine(
    (t) => {
      const n = TEMPLATE_REQUIRED_STATS.filter((k) => t[k] !== undefined).length
      return n === 0 || n === TEMPLATE_REQUIRED_STATS.length
    },
    { message: 'a template has every stat (cargo, strength, toughness, buildCost, buildSeconds, wreckLayout) or none (ranges only)' },
  )
  .refine((t) => TEMPLATE_REQUIRED_STATS.some((k) => t[k] !== undefined) || Object.keys(templateRangeFields).some((k) => t[k as keyof typeof templateRangeFields] !== undefined), {
    message: 'a template needs stats or a class range',
  })
export type HullTemplateCell = z.infer<typeof hullTemplateSchema>
/** A cell with its stats (what the compile rates a line hull from). */
export type HullTemplate = HullTemplateCell & Required<Pick<HullTemplateCell, (typeof TEMPLATE_REQUIRED_STATS)[number]>>

/** True when the cell carries the stats a line hull compiles from (false = ranges only, variants only). */
export function templateHasStats(t: HullTemplateCell): t is HullTemplate {
  return TEMPLATE_REQUIRED_STATS.every((k) => t[k] !== undefined)
}

/** Per-hull overrides of the compiled template values (set after the doctrine, so they are final). Stats only. */
const hullOverridesSchema = z.strictObject(templateStatFields).partial()
export type HullOverrides = z.infer<typeof hullOverridesSchema>

/** The row flags the compile stamps (spec §6.1); `retired` keeps a row that is no longer built. */
const flagFields = {
  export: z.boolean().optional(),
  story: z.boolean().optional(),
  unique: z.boolean().optional(),
  relic: z.boolean().optional(),
  retired: z.boolean().optional(),
}
export const HULL_FLAG_KEYS = ['export', 'story', 'unique', 'relic', 'retired'] as const

const roleSchema = z.strictObject({
  /** Band → role class name (checked against `SHIP_CLASS_NAMES` by P2b2). The role's bands are its keys. */
  classByBand: z.partialRecord(bandSchema, idString).refine((r) => Object.keys(r).length > 0, { message: 'a role needs a band' }),
  device: idString,
  aiJobs: z.array(z.enum(ROLE_IDS)).refine((a) => new Set(a).size === a.length, { message: 'duplicate aiJobs entry' }),
  /** Band rating templates: empty until P4b1 authors the first new rows (P2a2 compiles them on fixtures). */
  templates: z.partialRecord(bandSchema, hullTemplateSchema).optional(),
})

const doctrineSchema = z.strictObject({
  cargo: multiplier.optional(),
  strength: multiplier.optional(),
  toughness: multiplier.optional(),
  buildCost: multiplier.optional(),
})
export type Doctrine = z.infer<typeof doctrineSchema>

const makerSchema = z
  .strictObject({
    kind: z.enum(MAKER_KINDS),
    name: z.string().min(1),
    doctrine: doctrineSchema.optional(),
    /** The shipyard's doctrine display string. */
    line: z.string().min(1).optional(),
  })
  .refine((m) => (m.kind === 'operator' ? m.doctrine === undefined : m.doctrine !== undefined && m.line !== undefined), {
    message: 'a manufacturer or rebuilder needs a doctrine and a line; an operator has no doctrine in 1.0',
  })

const aiJobList = z.array(z.enum(ROLE_IDS)).refine((a) => new Set(a).size === a.length, { message: 'duplicate AI job' })

const lineHullSchema = z.strictObject({
  id: idString,
  band: bandSchema,
  /** The hull is an existing `hulls.json` row (same id) and keeps its authored stats. */
  legacy: z.boolean().optional(),
  model: z.string().min(1),
  /** The `ships.json` def; equals the row's `shipDef`. */
  def: idString,
  ...flagFields,
  /** A new (non-legacy) hull's overrides of its role template × doctrine. */
  overrides: hullOverridesSchema.optional(),
  /** The wing cap a ship of this hull leads (the Mastiff 5); absent = `fleet.json wing.size`. */
  wingSize: z.int().positive().optional(),
  /** M10 P4a1a: false = never sold in a shipyard (the cutter, the courier; P4a6 stamps it). Absent = sold. */
  sale: z.boolean().optional(),
  /** M10 P4b1a1: the AI jobs the compile stamps as the row's `roles` instead of the line role's `aiJobs` (new rows only). */
  roles: aiJobList.optional(),
  /** M10 P4b1a1 (P2 hand-off): the art target length in m, stamped on the row for P5's readouts. */
  lengthM: z.number().positive().optional(),
})

const lineSchema = z.strictObject({
  id: lineId,
  maker: factionSchema,
  /** Absent only on a story line (the Halcyon yacht). */
  role: hullRoleSchema.optional(),
  hulls: z.array(lineHullSchema).min(1),
})

const unlinedSchema = z.strictObject({
  id: idString,
  band: bandSchema,
  def: idString,
  ...flagFields,
})

/**
 * A variant of a hull (spec §6.1): its base row (a line hull, an unlined hull or an earlier variant), then its own
 * `def`, `overrides`, flags and, if set, its hull `role` (the class at the base's band and that role's jobs).
 * P4b1 adds the parts fields.
 */
const variantSchema = z.strictObject({
  id: idString,
  base: idString,
  def: idString,
  role: hullRoleSchema.optional(),
  overrides: hullOverridesSchema.optional(),
  ...flagFields,
  /** M10 P4b1a1 (review M2): stamps `sale: false` when false, `sale` unstamped when true; absent = the base's. */
  sale: z.boolean().optional(),
  /** M10 P4b1a1: the row's AI jobs, instead of `role`'s `aiJobs` or the base's roles (`[]` = no AI job). */
  roles: aiJobList.optional(),
})

// M10 P4a1a (spec §4.7–§4.9, §6.1; plan 2026-10-11-m10-p4a §3.1): every P4a switch and table, shipped off or
// inert. Nothing reads them until W2/W3's code cards; P4a7a/P4a7b flip them one commit at a time.

/** A licence (spec §4.7): `licensee`'s yards build `maker`'s line `line`. */
const licenceSchema = z.strictObject({ maker: factionSchema, licensee: factionSchema, line: lineId })
export type HullLicence = z.infer<typeof licenceSchema>

/** The royalty a non-maker yard pays the maker per build (spec §4.7). `rate` × `hullValue`; 0 = off. */
const royaltySchema = z.strictObject({ rate: z.number().min(0).max(0.5), stopAtWar: z.boolean() })

const bandSet = z.array(bandSchema).refine((a) => new Set(a).size === a.length, { message: 'duplicate band' })
const standing = z.number().min(-100).max(100)

/** Ship trade by M5 relation (spec §4.8). The player rows are standing thresholds with the yard's owner. */
const embargoSchema = z.strictObject({
  enabled: z.boolean(),
  /** The price multiplier's increment at economic relations (0.15 = +15%). */
  economicSurcharge: z.number().min(0).max(1),
  /** Bands a yard refuses at skirmish. */
  skirmishRefuses: bandSet,
  /** War start cancels the pair's queued AI orders (its own flip after `enabled`, review M7). */
  cancelAtWarStart: z.boolean(),
  /**
   * M10 P4a W5c (D-TBD): the last-resort yard. When every reachable yard that can build an AI job refuses it at war
   * (and no other yard takes it), the best of those war yards takes the order at skirmish terms (an L/XL-only row is
   * still refused) and the economic surcharge, so an embargo never leaves a job with no yard. Inert while `enabled` is
   * false; a bisect handle (false = P4a7d's refusal).
   */
  warFallback: z.boolean().default(true),
  player: z
    .strictObject({ economicBelow: standing, skirmishBelow: standing, warBelow: standing })
    .refine((p) => p.warBelow < p.skirmishBelow && p.skirmishBelow < p.economicBelow, {
      message: 'embargo.player needs warBelow < skirmishBelow < economicBelow',
    }),
})

/** Outlaws (raider, guardian) buy only these bands, export patterns only, at any yard (spec §4.8). */
const outlawRuleSchema = z.strictObject({ bands: bandSet.refine((a) => a.length > 0, { message: 'outlawRule needs a band' }), exportOnly: z.boolean() })

export const CAPACITY_METRICS = ['cargo', 'strength'] as const
const roleIdSchema = z.enum(ROLE_IDS)
/** Capacity units in job counts (spec §4.9; plan §0.3): from `unitBand`, clamp(round(metric / roleUnit), 1, maxUnits). */
const capacitySchema = z.strictObject({
  enabled: z.boolean(),
  unitBand: bandSchema,
  maxUnits: z.int().min(1),
  /** Keys exactly `ROLE_IDS` (an exhaustive enum record). */
  roleUnit: z.record(roleIdSchema, z.number().positive()),
  metric: z.record(roleIdSchema, z.enum(CAPACITY_METRICS)),
})

/** Every hull id `hull-lines.json` defines: line hulls, variants and unlined hulls, in file order. */
export function hullIdsOf(f: { lines: readonly { hulls: readonly { id: string }[] }[]; variants: readonly { id: string }[]; unlined: readonly { id: string }[] }): string[] {
  return [...f.lines.flatMap((l) => l.hulls.map((h) => h.id)), ...f.variants.map((v) => v.id), ...f.unlined.map((u) => u.id)]
}

export const hullLinesSchema = z
  .strictObject({
    comment: z.string().optional(),
    sizes: z.strictObject({ S: sizeSchema, M: sizeSchema, L: sizeSchema, XL: sizeSchema }),
    roles: z.record(hullRoleSchema, roleSchema),
    makers: z.partialRecord(factionSchema, makerSchema),
    lines: z.array(lineSchema),
    unlined: z.array(unlinedSchema),
    variants: z.array(variantSchema),
    licences: z.array(licenceSchema),
    royalty: royaltySchema,
    embargo: embargoSchema,
    outlawRule: outlawRuleSchema,
    /** The yard-level gate (spec §4.9; computed from `sizes.<band>.minYardLevel`, nothing stored). */
    canBuild: z.strictObject({ enabled: z.boolean() }),
    capacity: capacitySchema,
    /**
     * The L hull's weight in AI mixes (spec §4.9: 0 until P4b5). M10 P4b1a1: `cap` = faction → AI job → the most
     * flagships (L) of that job the faction keeps; absent = 0. Read from P4b5.
     */
    flagship: z.strictObject({ lWeight: z.number().min(0).max(1), cap: z.partialRecord(factionSchema, z.partialRecord(roleIdSchema, z.int().min(0))).optional() }),
    /**
     * M10 P4b1a1: per-buyer AI mixes (buyer faction → AI job → hull id → weight > 0) beside `roles.json`'s. Inert until
     * P4b5a. The hull checks (exists, not retired, its roles list the job, an L hull only for its maker) run at load in
     * `data.ts`, where the compiled rows are.
     */
    buyerMixes: z.partialRecord(factionSchema, z.partialRecord(roleIdSchema, z.record(idString, z.number().positive()))).optional(),
  })
  .refine((f) => new Set(f.lines.map((l) => l.id)).size === f.lines.length, { message: 'duplicate line id' })
  .refine((f) => f.lines.every((l) => f.makers[l.maker] !== undefined), { message: 'a line maker is not in makers' })
  .refine(
    (f) => {
      const ids = hullIdsOf(f)
      return new Set(ids).size === ids.length
    },
    { message: 'a hull id appears more than once across lines, variants and unlined' },
  )
  .refine(
    (f) => {
      const defs = [...f.lines.flatMap((l) => l.hulls.map((h) => h.def)), ...f.variants.map((v) => v.def), ...f.unlined.map((u) => u.def)]
      return new Set(defs).size === defs.length
    },
    { message: 'a ship def is used by more than one hull' },
  )
  .refine(
    (f) =>
      f.licences.every(
        (l) => l.maker !== l.licensee && f.makers[l.maker] !== undefined && f.makers[l.licensee] !== undefined && f.lines.some((x) => x.id === l.line && x.maker === l.maker),
      ),
    { message: "a licence needs two different makers and a line of the licence's maker" },
  )
  .refine((f) => new Set(f.licences.map((l) => `${l.licensee}/${l.line}`)).size === f.licences.length, { message: 'duplicate licence' })
  .refine(
    (f) =>
      f.lines.every((l) =>
        l.hulls.every((h) => {
          if (h.legacy === true || l.role === undefined) return true
          const t = f.roles[l.role].templates?.[h.band]
          return t === undefined || templateHasStats(t)
        }),
      ),
    { message: 'a new line hull sits on a ranges-only template cell (only variants may use one)' },
  )

export type HullLines = z.infer<typeof hullLinesSchema>
export type HullLine = HullLines['lines'][number]
export type LineHull = HullLine['hulls'][number]

export const hullOrderSchema = z
  .strictObject({
    comment: z.string().optional(),
    order: z.array(idString).min(1),
  })
  .refine((o) => new Set(o.order).size === o.order.length, { message: 'duplicate hull id in the order lock' })

export type HullOrder = z.infer<typeof hullOrderSchema>

/** Parses and validates `hull-lines.json` (throws a ZodError naming the bad field). */
export function loadHullLines(raw: unknown = hullLinesJson): HullLines {
  return hullLinesSchema.parse(raw)
}

/** Parses and validates `hull-order.json`. */
export function loadHullOrder(raw: unknown = hullOrderJson): HullOrder {
  return hullOrderSchema.parse(raw)
}
